import { type KeyboardEvent, type ReactNode } from "react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { MISSING_DATE_LABEL, toValidDate, type DateLike } from "@/lib/safeDate";

import { formatPersonName } from "../contacts/contactModel";
import type { Contact } from "../types";
import { contactCardPath } from "./constants";
import { DUE_LABEL_KEYS, dueBucket, type DueBucket } from "./dates";
import { rowClass } from "./shell";

export const contactName = (contact: {
  first_name?: string | null;
  last_name?: string | null;
}) => formatPersonName(contact.first_name, contact.last_name);

export const StatusBadge = ({ status }: { status?: string | null }) => {
  if (!status) return null;
  return <Badge variant={status === "Warm" ? "ok" : "chip"}>{status}</Badge>;
};

export const DueBadge = ({ bucket }: { bucket: DueBucket }) => {
  const translate = useTranslate();
  const variant =
    bucket === "overdue" ? "warn" : bucket === "today" ? "ok" : "chip";
  return <Badge variant={variant}>{translate(DUE_LABEL_KEYS[bucket])}</Badge>;
};

/** Dated tasks keep their bucket. A missing deadline is Zonder datum, not Later. */
export const OpenTaskDue = ({ dueDate }: { dueDate: DateLike }) => {
  if (!toValidDate(dueDate)) {
    return <Badge variant="chip">{MISSING_DATE_LABEL}</Badge>;
  }
  return <DueBadge bucket={dueBucket(dueDate)} />;
};

export const ContactNameLink = ({
  contact,
}: {
  contact: Pick<Contact, "id" | "first_name" | "last_name">;
}) => (
  <Link
    to={contactCardPath(contact.id)}
    className="text-[14px] font-[650] text-foreground hover:underline"
  >
    {contactName(contact)}
  </Link>
);

const activateTileKey = (event: KeyboardEvent<HTMLAnchorElement>) => {
  if (event.key !== "Enter" && event.key !== " ") return;
  // Only the focused tile. A key that bubbled from a child or a portal
  // must keep its own default, including Enter in a dialog field.
  if (event.target !== event.currentTarget) return;
  event.preventDefault();
  event.currentTarget.click();
};

/** The whole row opens the contact. Wrap buttons in `ActionTileControls`. */
export const ActionTile = ({
  contact,
  className,
  children,
}: {
  contact: Pick<Contact, "id" | "first_name" | "last_name">;
  className?: string;
  children: ReactNode;
}) => (
  <li className="list-none">
    <Link
      to={contactCardPath(contact.id)}
      aria-label={contactName(contact)}
      onKeyDown={activateTileKey}
      className={cn(
        rowClass,
        "group flex w-full cursor-pointer text-left text-inherit no-underline outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      {children}
    </Link>
  </li>
);

export const ActionTileControls = ({ children }: { children: ReactNode }) => (
  <div
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
    }}
    onKeyDown={(event) => event.stopPropagation()}
  >
    {children}
  </div>
);

export const ActionTileName = ({
  contact,
}: {
  contact: Pick<Contact, "id" | "first_name" | "last_name">;
}) => (
  <span
    aria-hidden
    className="text-[14px] font-[650] text-foreground group-hover:underline"
  >
    {contactName(contact)}
  </span>
);
